import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/ui/alert-dialog.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Checkbox } from "@/shared/ui/checkbox.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { cn } from "@/shared/lib/cn.js";
import { TRIGGERS, TRIGGER_MATCHES, WEEK_DAYS } from "../automation-constants.js";
import { useCreateAutomation, useUpdateAutomation, useDeleteAutomation } from "../use-automations.js";

/**
 * Diálogos da tela de Automação: nova jornada, editar jornada, janela de
 * envio e confirmação de exclusão. Todos com os mesmos campos do app atual.
 */

// ------------------------------------------------------------ nova jornada

export function NewJourneyDialog({ open, onClose }) {
  const criar = useCreateAutomation();
  const [nome, setNome] = useState("");
  const [gatilho, setGatilho] = useState(TRIGGERS[0].id);

  function submeter(evento) {
    evento.preventDefault();
    const escolhido = TRIGGERS.find((t) => t.id === gatilho);
    criar.mutate(
      { name: nome.trim(), trigger: `${escolhido.label} — ${escolhido.sub}` },
      {
        onSuccess: () => {
          setNome("");
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent>
        <form onSubmit={submeter}>
          <DialogHeader>
            <DialogTitle>Nova jornada</DialogTitle>
            <DialogDescription>
              Crie um fluxo de mensagens para um procedimento ou campanha.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="journey-name">Nome da jornada</Label>
              <Input
                id="journey-name"
                required
                maxLength={120}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Jornada Implante"
              />
            </div>

            <div className="space-y-2">
              <Label>Gatilho</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {TRIGGERS.map((t) => {
                  const ativo = gatilho === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setGatilho(t.id)}
                      aria-pressed={ativo}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        ativo ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
                      )}
                    >
                      <t.icon className={cn("mt-0.5 h-4 w-4", ativo && "text-primary")} />
                      <div>
                        <p className="text-sm font-medium">{t.label}</p>
                        <p className="text-xs text-muted-foreground">{t.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={criar.isPending || !nome.trim()}>
              {criar.isPending ? <Spinner /> : null}
              Criar jornada
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------- editar jornada

export function EditJourneyDialog({ automation, open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent>
        {open && automation && (
          <EditJourneyForm key={automation.id} automation={automation} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditJourneyForm({ automation, onClose }) {
  const atualizar = useUpdateAutomation();
  const [form, setForm] = useState(() => ({
    name: automation.name,
    trigger: automation.trigger,
    triggerKeywords: automation.triggerKeywords ?? "",
    triggerMatch: automation.triggerMatch ?? "any",
  }));

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  function submeter(evento) {
    evento.preventDefault();
    atualizar.mutate({ id: automation.id, ...form }, { onSuccess: onClose });
  }

  return (
    <form onSubmit={submeter}>
      <DialogHeader>
        <DialogTitle>Editar jornada</DialogTitle>
        <DialogDescription>Nome, gatilho e palavras-chave de disparo.</DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="edit-name">Nome</Label>
          <Input
            id="edit-name"
            required
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-trigger">Gatilho</Label>
          <Input
            id="edit-trigger"
            value={form.trigger}
            onChange={(e) => set("trigger")(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-keywords">Palavras-chave</Label>
          <Input
            id="edit-keywords"
            value={form.triggerKeywords}
            onChange={(e) => set("triggerKeywords")(e.target.value)}
            placeholder="implante, dente, prótese"
          />
          <p className="text-xs text-muted-foreground">Separe por vírgula.</p>
        </div>

        <div className="space-y-2">
          <Label>Como comparar</Label>
          <Select value={form.triggerMatch} onValueChange={set("triggerMatch")}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIGGER_MATCHES.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={atualizar.isPending}>
          {atualizar.isPending ? <Spinner /> : null}
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}

// ----------------------------------------------------------- janela de envio

export function ScheduleDialog({ automation, open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent>
        {open && automation && (
          <ScheduleForm key={automation.id} automation={automation} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScheduleForm({ automation, onClose }) {
  const atualizar = useUpdateAutomation();
  const [dias, setDias] = useState(() => automation.schedule.days ?? []);
  const [inicio, setInicio] = useState(automation.schedule.start ?? "09:00");
  const [fim, setFim] = useState(automation.schedule.end ?? "19:00");

  function alternarDia(dia) {
    setDias((atuais) =>
      atuais.includes(dia) ? atuais.filter((d) => d !== dia) : [...atuais, dia],
    );
  }

  function submeter(evento) {
    evento.preventDefault();
    atualizar.mutate(
      {
        id: automation.id,
        // Grava na ordem da semana, não na ordem em que foram clicados.
        scheduleDays: WEEK_DAYS.filter((d) => dias.includes(d)),
        scheduleStart: inicio,
        scheduleEnd: fim,
      },
      { onSuccess: onClose },
    );
  }

  const horarioValido = inicio < fim;

  return (
    <form onSubmit={submeter}>
      <DialogHeader>
        <DialogTitle>Janela de envio</DialogTitle>
        <DialogDescription>
          As mensagens desta jornada só são enviadas dentro deste horário.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Dias da semana</Label>
          <div className="flex flex-wrap gap-3">
            {WEEK_DAYS.map((dia) => (
              <label key={dia} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox checked={dias.includes(dia)} onCheckedChange={() => alternarDia(dia)} />
                {dia}
              </label>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="schedule-start">Início</Label>
            <Input
              id="schedule-start"
              type="time"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="schedule-end">Fim</Label>
            <Input id="schedule-end" type="time" value={fim} onChange={(e) => setFim(e.target.value)} />
          </div>
        </div>

        {!horarioValido && (
          <p className="text-xs text-destructive">O horário final precisa ser depois do inicial.</p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={atualizar.isPending || !horarioValido}>
          {atualizar.isPending ? <Spinner /> : null}
          Salvar janela
        </Button>
      </DialogFooter>
    </form>
  );
}

// ------------------------------------------------------------ confirmações

export function DeleteJourneyDialog({ automation, open, onClose }) {
  const excluir = useDeleteAutomation();

  return (
    <AlertDialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir jornada?</AlertDialogTitle>
          <AlertDialogDescription>
            A jornada <strong>{automation?.name}</strong> e todas as suas etapas serão removidas.
            Esta ação não pode ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(evento) => {
              evento.preventDefault();
              excluir.mutate(automation.id, { onSuccess: onClose });
            }}
            disabled={excluir.isPending}
          >
            {excluir.isPending ? <Spinner /> : null}
            Excluir
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
